/*
 * ─── ที่จับชิ้นงานเล็ก: the rules (no Fabric here, so the tests can load it) ───────────────────────────────────────────
 * Measured on the real editor (Fabric 5.3, touch area 48 px per handle): a piece smaller than 48 px on the screen
 * resized instead of moving when dragged from its middle, and a 60–80 px piece was 87–97 % handle area.
 * Choice ก (Canva-like):
 *   - a finger inside the piece moves it — a handle only grabs from inside within `reach` of it, which shrinks with the
 *     piece, so its middle always moves;
 *   - where several handles' areas overlap, the nearest handle wins (not Fabric's fixed order, where corners always won);
 *   - a short edge hides its middle handle (a text box keeps ml/mr, a line all four: their main handles);
 *   - a piece small enough to hide under a finger gets a ✥ grip below it to drag it by.
 * Sizes are screen px (the piece's size × zoom), so they follow zooming without any extra work.
 */

/** an edge shorter than this on screen hides its middle handle */
export const SIDE_MIN = 64;
/** a piece whose longer side is shorter than this on screen (it hides under a fingertip) gets the ✥ grip */
export const GRIP_MAX = 44;
/** how far below the piece the grip sits (screen px, from the bottom edge to the grip's middle) */
export const GRIP_GAP = 34;
/** the grip's drawn size (screen px) */
export const GRIP_SIZE = 28;

export type HandleKey = 'tl' | 'tr' | 'bl' | 'br' | 'ml' | 'mr' | 'mt' | 'mb' | 'mtr' | 'grip' | string;

/** what kind of piece: a text box's side handles set its width; a line's side handles stretch its length */
export type PieceKind = 'text' | 'line' | 'other';

/**
 * whether handle `key` is shown on a piece `w` × `h` screen px.
 * A text box keeps ml/mr and a line keeps all four side handles even when short — they are those pieces' main handles
 * (a horizontal line is only its stroke tall, so its ml/mr would otherwise always hide); the nearest-handle rule keeps
 * them reachable next to the corners.
 */
export function handleShown(key: HandleKey, w: number, h: number, kind: PieceKind): boolean {
  if (key === 'grip') return Math.max(w, h) < GRIP_MAX;
  if (kind === 'line') return true;
  if (key === 'mt' || key === 'mb') return w >= SIDE_MIN;
  if (key === 'ml' || key === 'mr') return kind === 'text' || h >= SIDE_MIN;
  return true;
}

/**
 * how far inside the piece a handle still grabs: its half touch size, but never more than a sixth of the shorter side —
 * so at least the middle two thirds of the piece always moves (with a quarter, an 80 px piece was still 54 % handles)
 */
export const REACH_PART = 6;
export function reachInside(minSide: number, half: number): number {
  return Math.max(0, Math.min(half, minSide / REACH_PART));
}

/** a handle whose touch area holds the point, with the distance from the point to the handle's middle (screen px) */
export interface HandleHit { key: HandleKey; d: number }

/**
 * Which handle the point grabs, or null = move the piece.
 * `hits` in Fabric's order (the one drawn on top first); `inside` = the point is inside the piece's frame.
 */
export function pickHandle(hits: HandleHit[], inside: boolean, minSide: number, half: number): HandleKey | null {
  const reach = reachInside(minSide, half);
  let best: HandleHit | null = null;
  for (const h of hits) {
    if (inside && h.d > reach) continue;
    if (!best || h.d < best.d) best = h;
  }
  return best ? best.key : null;
}
